/**
 * Generic CRUD helpers for a single table.
 * You can extend per-feature with domain-specific helpers.
 */
// export function createRepo(db, tableName, { keyProp = "id" } = {}) {
export function createRepo(db, tableName, opts = {}) {
  const table = db.table(tableName);

  const repo = {
    table,
    keyProp: opts.keyProp || "id",
    model: opts.model || null,

    /**
     * Add a single item.
     * @param {object} item - The item to add.
     * @returns {Promise<number>} The key of the added item.
     */
    add: async (item) => {
      return await table.add(item);
    },

    /**
     * Retrieve a single item from the table by its primary key.
     *
     * @param {number|string} id - The primary key of the item to retrieve.
     * @returns {Promise<object|undefined>} A promise that resolves to the item if found, or undefined if no item exists with the given key.
     * @throws {Error} If the database operation fails.
     * @example
     * const item = await repo.getById(1);
     * if (item) {
     *   console.log('Found:', item);
     * } else {
     *   console.log('Item not found');
     * }
     *
     * @example
     * // Using with optional chaining
     * const name = (await repo.getById(1))?.name ?? 'Unknown';
     */
    getById: async (id) => {
      return await table.get(id);
    },

    /**
     * Retrieve all items from the table.
     *
     * @returns {Promise<Array<ItemType>>} A promise that resolves to an array of all items.
     * @throws {Error} If the database operation fails.
     * @example
     * const allItems = await repo.getAll();
     * console.log(allItems); // [{id: 1, ...}, {id: 2, ...}]
     */
    getAll: async () => {
      return await table.toArray();
    },

    /**
     * Add or update an item in the table (upsert operation).
     *
     * Uses the primary key to determine if the item should be inserted or updated.
     * @param {object} item - The item to add or update. Must include the primary key if updating.
     * @returns {Promise<number|string>} A promise that resolves to the primary key of the item.
     * @throws {Error} If the database operation fails.
     * @example
     * // Add new item
     * const key = await repo.put({ name: 'New Item' });
     *
     * // Update existing item
     * await repo.put({ id: 1, name: 'Updated Item' });
     */
    put: async (item) => {
      return await table.put(item);
    },

    /**
     * Delete an item from the table by its primary key.
     *
     * If the item doesn't exist, the operation completes successfully without error.
     * @param {number|string} primaryKey - The primary key of the item to delete.
     * @returns {Promise<void>} A promise that resolves when the deletion is complete.
     *    Silent success if item does not exist.
     * @throws {Error} If the database operation fails.
     * @example
     * await repo.delete(1); // Deletes item with primary key 1
     */
    delete: async (primaryKey) => {
      await table.delete(primaryKey);
    },

    /**
     * Add multiple items to the table in a single operation.
     * This is more efficient than calling add() multiple times.
     * @param {Array<object>} items - An array of items to add to the table.
     * @returns {Promise<number|string>} A promise that resolves to the primary key of the last added item.
     * @throws {Error} If any item has a primary key that already exists in the table, the entire operation fails and no items are added (atomic operation).
     * @throws {Error} If the database operation fails.
     * @example
     * // Add multiple items at once
     * const lastKey = await repo.bulkAdd([
     *   { name: 'Item 1', value: 100 },
     *   { name: 'Item 2', value: 200 },
     *   { name: 'Item 3', value: 300 }
     * ]);
     * console.log('Last key:', lastKey);
     *
     * @example
     * // Handle duplicate key error
     * try {
     *   await repo.bulkAdd(items);
     * } catch (error) {
     *   if (error.name === 'ConstraintError') {
     *     console.error('One or more items already exist');
     *   }
     * }
     */
    bulkAdd: async (items) => {
      return await table.bulkAdd(items);
    },

    /**
     * Retrieve all items from the table that match a specific indexed field value.
     * Uses an indexed query for optimal performance.
     * @param {string} indexName - The name of the indexed field to query. Must be defined in the Dexie schema.
     * @param {*} value - The value to match against the indexed field. Type should match the indexed field's type.
     * @returns {Promise<Array<object>>} A promise that resolves to an array of all matching items. Returns an empty array if no matches are found.
     * @throws {Error} If the indexName doesn't exist in the table schema.
     * @throws {Error} If the database operation fails.
     * @example
     * // Assuming a 'users' table with an index on 'email'
     * const usersByEmail = await repo.filterByIndex('email', 'john@example.com');
     * console.log(usersByEmail); // [{ id: 1, email: 'john@example.com', name: 'John' }]
     *
     * @example
     * // Query by status index
     * const activeItems = await repo.filterByIndex('status', 'active');
     * if (activeItems.length === 0) {
     *   console.log('No active items found');
     * }
     *
     * @example
     * // Query by numeric index
     * const itemsWithPriority = await repo.filterByIndex('priority', 1);
     */
    filterByIndex: async (indexName, value) => {
      return await table.where(indexName).equals(value).toArray();
    },
  };

  return repo;

  // return {
  //   table,
  //   tableName,
  //   async getStoreObject(tableName, defaultValue = {}) {
  //     const row = await table.get(tableName);
  //     return row ?? defaultValue;
  //   },
  //   async get(id, defaultValue = null) {
  //     const row = await table.get(id);
  //     return row ?? defaultValue;
  //     // return table.get(id);
  //   },
  //   async list(where = null) {
  //     if (!where) return table.toArray();
  //     // `where` can be a predicate (item => boolean)
  //     return table.filter(where).toArray();
  //   },
  //   async put(item) {
  //     if (!item[keyProp]) throw new Error(`[${tableName}] missing ${keyProp}`);
  //     item.modified = Date.now();
  //     return table.put(item);
  //     // return table.put(item);
  //   },
  //   async bulkPut(items) {
  //     return table.bulkPut(items);
  //   },
  //   async update(id, patch) {
  //     return table.update(id, patch);
  //   },
  //   async remove(id) {
  //     return table.delete(id);
  //   },
  //   async clear() {
  //     return table.clear();
  //   },

  //   /** Utility: Last-write-wins upsert with modified */
  //   async upsert(item, touch = true) {
  //     const now = Date.now();
  //     const existing = item[keyProp] ? await table.get(item[keyProp]) : null;

  //     if (!existing) {
  //       return table.put({ ...item, ...(touch ? { modified: now } : {}) });
  //     }
  //     const newer =
  //       !existing.modified || (item.modified || 0) >= existing.modified;
  //     if (newer) {
  //       return table.put({
  //         ...existing,
  //         ...item,
  //         ...(touch ? { modified: now } : {}),
  //       });
  //     }
  //     return existing[keyProp];
  //   },
  // };
}
